import React, { ReactElement, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { ToggleV2 } from 'swui';
import { getCookie, setCookie, isDarkMode, convertMinutesToMs } from '../../../utils/helpers';
import { detectGpc } from '../../../utils/gpc';
import useCookieConsentCheck from '../../../hooks/useCookieConsentCheck';
import { updateCookieConsent } from '../../../utils/dataLayer';
import { toaster } from '../../../common';
import colors from '../../../theme/colors';
import breakpoints from '../../../theme/material/breakpoints';
import typography from '../../../theme/typography';
import { RootState } from '../../../store';
import { updateCookieConsentValue } from '../../../actions/settingActions';

const PreferencesContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '1.3rem 1.5rem 1.3rem 2rem',
  gap: '20px',
  [breakpoints.down('sm')]: {
    padding: '1rem',
  },
});

const LineDivider = styled('div')({
  paddingBottom: '20px',
  borderBottom: '1px solid #DAE1E9',
  width: '100%',
});

const PageTitle = styled('h1')(() => ({
  fontFamily: 'Montserrat',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '15px',
  lineHeight: '20px',
  letterSpacing: '-0.3px',
  margin: 0,
  color: isDarkMode() ? typography.dark.primaryText : typography.light.primaryText,
}));

const Section = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

const SectionTitle = styled('h2')(() => ({
  fontFamily: 'Montserrat',
  fontWeight: 600,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '21px',
  letterSpacing: '-0.3px',
  margin: 0,
  flex: 1,
  color: isDarkMode() ? typography.dark.primaryText : typography.light.primaryText,
}));

const ToggleContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  gap: '20px',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const Description = styled('p')(() => ({
  fontFamily: 'Montserrat',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  color: isDarkMode() ? typography.dark.primaryText : typography.light.primaryText,
  margin: 0,
}));

const StyledToggle = styled(ToggleV2)({
  width: '56px !important',
  height: '28px !important',
  opacity: 1,
  flexShrink: 0,
});

const PrivacyPolicyLink = styled('a')(() => ({
  display: 'inline-block',
  width: 'fit-content',
  color: `${colors.blue[500]} !important`,
  fontSize: '13px',
  textDecoration: 'underline !important',
  fontWeight: 400,
}));

const GpcStatusLabel = styled('h3')(() => ({
  fontWeight: 600,
  fontSize: '15px',
  lineHeight: 0,
  color: isDarkMode() ? typography.dark.primaryText : typography.light.primaryText,
}));

const GpcStatusText = styled('span')(() => ({
  fontWeight: 400,
  fontSize: '15px',
  marginTop: '-15px',
  color: isDarkMode() ? typography.dark.primaryText : typography.light.primaryText,
}));

const PrivacyPreferences = (): ReactElement => {
  const dispatch = useDispatch();
  const { isGpcActive: gpcActiveFromHook } = useCookieConsentCheck();
  const gpcDetection = detectGpc();
  const isGpcActive = gpcActiveFromHook || gpcDetection.isActive;

  // Get cookie consent settings from Redux
  const settings = useSelector((state: RootState) => state.settings);
  const cookieConsentConfig = settings?.cookie_consent;
  const cookieConsentValue = useSelector((state: RootState) => state.settings?.cookieConsentValue);

  // Task 3.2: Implement initial toggle state logic
  // accepted → toggle OFF, rejected → toggle ON, closed/not set → toggle ON
  const getInitialToggleState = (): boolean => {
    // First check Redux state
    if (cookieConsentValue === 'accepted') {
      return false; // Toggle OFF (allow)
    }

    if (cookieConsentValue === 'rejected') {
      return true; // Toggle ON (deny)
    }

    // Fallback to cookie value if Redux state not set
    const cookieValue = getCookie('cookie_consent');

    if (!cookieValue || cookieValue === 'closed') {
      return true; // Toggle ON (deny)
    }

    if (cookieValue === 'accepted') {
      return false; // Toggle OFF (allow)
    }

    if (cookieValue === 'rejected') {
      return true; // Toggle ON (deny)
    }

    return true; // Default to ON (deny)
  };

  const [isToggleOn, setIsToggleOn] = useState<boolean>(getInitialToggleState());

  // Task 3.4: Add GPC precedence logic and sync with Redux
  // When GPC is active, override toggle state and disable interaction
  // When Redux state changes, update toggle
  useEffect(() => {
    if (isGpcActive) {
      setIsToggleOn(true); // Force toggle ON when GPC is active
    } else {
      // When GPC is not active, sync with Redux state
      setIsToggleOn(getInitialToggleState());
    }
  }, [isGpcActive, cookieConsentValue]);

  // Task 3.5: Implement cross-tab synchronization
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'cookie_consent_changed') {
        // Cookie consent was changed in another tab
        const newValue = getCookie('cookie_consent');

        // Update Redux state to sync across tabs
        if (newValue === 'accepted') {
          dispatch(updateCookieConsentValue('accepted'));
        } else if (newValue === 'rejected') {
          dispatch(updateCookieConsentValue('rejected'));
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch]);

  // Task 3.3: Wire toggle onChange handler
  const handleToggleChange = () => {
    // Don't allow toggle if GPC is active
    if (isGpcActive) {
      return;
    }

    const newToggleState = !isToggleOn;

    setIsToggleOn(newToggleState);

    // Update cookie_consent cookie with expiration from settings or default to 1 year
    const cookieValue = newToggleState ? 'rejected' : 'accepted';
    const expiryMinutes = cookieConsentConfig?.actionExpiryMins || 525600; // 1 year default
    const expiryMs = convertMinutesToMs(expiryMinutes);

    setCookie('cookie_consent', cookieValue, false, expiryMs);

    // Update Redux state
    dispatch(updateCookieConsentValue(cookieValue));

    // Update gtag consent settings
    updateCookieConsent(newToggleState ? 'denied' : 'granted');

    // Trigger cross-tab sync
    localStorage.setItem('cookie_consent_changed', Date.now().toString());

    // Task 4.0: Show toast notification
    if (newToggleState) {
      toaster.success('Successfully opted-out of third-party cookies');
    } else {
      toaster.success('Your preferences have been updated');
    }
  };

  return (
    <PreferencesContainer>
      <LineDivider>
        <PageTitle>Manage how your information is used.</PageTitle>
      </LineDivider>

      {/* Cookie Preferences Section - First */}
      <Section>
        <ToggleContainer>
          <SectionTitle>
            Do Not Sell or Share
            <br />
            My Personal Information
          </SectionTitle>
          <StyledToggle checked={isToggleOn} onChange={handleToggleChange} disabled={isGpcActive} />
        </ToggleContainer>
        <LineDivider />
        <Description>
          You may opt out of the sale or sharing of your personal information with third parties for cross-context
          behavioral advertising.
        </Description>
        <Description>
          When this setting is turned <b>on</b>, WhatsYourPrice will stop sharing your data with third-party partners
          for advertising purposes.
        </Description>
        <PrivacyPolicyLink href="https://www.whatsyourprice.com/privacy" target="_blank" rel="noopener noreferrer">
          Learn more in our Privacy Policy
        </PrivacyPolicyLink>
        <LineDivider />
      </Section>

      {/* GPC Section - Second */}
      <Section>
        <SectionTitle>Global Privacy Control (GPC)</SectionTitle>
        {isGpcActive ? (
          <>
            <GpcStatusLabel>GPC active:</GpcStatusLabel>
            <GpcStatusText>
              We received a Global Privacy Control signal and have applied your opt-out automatically.
            </GpcStatusText>
          </>
        ) : (
          <>
            <GpcStatusLabel>GPC not detected:</GpcStatusLabel>
            <GpcStatusText>GPC is not detected in your browser.</GpcStatusText>
          </>
        )}
      </Section>
    </PreferencesContainer>
  );
};

export default PrivacyPreferences;
